/**
 * The data table every list screen will use.
 *
 * PRESENTATIONAL BY CONTRACT. It draws rows it is given and knows nothing about
 * where they came from: no database, no business, no fetching. That is what lets
 * the same table serve customers, products, documents and payments without
 * growing a `if` per screen. All of its decisions are in `lib/table.ts`, which
 * is pure and tested — there is no React testing library here, and the
 * alternative to testing logic is not testing it.
 *
 * The header is a real `<button>` with `aria-sort`, so the table is usable from
 * a keyboard and a screen reader announces which column is sorted and which way.
 */
import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '../lib/cn';
import {
  UNSORTED,
  ariaSortFor,
  filterRows,
  sortRows,
  toggleSort,
  type Column,
  type SortState,
} from '../lib/table';

export interface DataTableProps<T> {
  readonly rows: readonly T[];
  readonly columns: readonly Column<T>[];
  /** Stable row identity. Without it, sorting a list of equal rows flickers. */
  readonly rowKey: (row: T) => string;
  readonly caption: string;
  /** What to search across. Defaults to every column. */
  readonly searchColumns?: readonly Column<T>[];
  readonly emptyMessage?: string;
  readonly onRowClick?: (row: T) => void;
  readonly className?: string;
}

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  caption,
  searchColumns,
  emptyMessage = 'Nothing to show yet.',
  onRowClick,
  className,
}: DataTableProps<T>) {
  const [sort, setSort] = useState<SortState>(UNSORTED);
  const [query, setQuery] = useState('');

  const searchable = searchColumns ?? columns;
  const visible = useMemo(
    () => sortRows(filterRows(rows, query, searchable), columns, sort.key, sort.dir),
    [rows, query, searchable, columns, sort],
  );

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.currentTarget.value)}
        placeholder={`Search ${caption.toLowerCase()}`}
        aria-label={`Search ${caption}`}
        className="w-full max-w-xs rounded border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-100 placeholder:text-slate-500"
      />

      <table className="w-full border-collapse text-left text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="border-b border-slate-800">
            {columns.map((column) => {
              const state = ariaSortFor(sort, column.key);
              const Icon =
                state === 'ascending' ? ArrowUp : state === 'descending' ? ArrowDown : ChevronsUpDown;
              return (
                <th
                  key={column.key}
                  scope="col"
                  aria-sort={state}
                  className={cn(
                    'px-3 py-2 font-medium text-slate-400',
                    column.align === 'right' ? 'text-right' : 'text-left',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => setSort((s) => toggleSort(s, column.key))}
                    className="inline-flex items-center gap-1 hover:text-slate-200"
                  >
                    {column.header}
                    <Icon className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {visible.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-3 py-6 text-center text-slate-500">
                {query.trim() === '' ? emptyMessage : `No ${caption.toLowerCase()} match “${query}”.`}
              </td>
            </tr>
          ) : (
            visible.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  'border-b border-slate-800/60',
                  onRowClick && 'cursor-pointer hover:bg-slate-800/40',
                )}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cn(
                      'px-3 py-2 text-slate-200',
                      column.align === 'right' ? 'text-right tabular-nums' : 'text-left',
                    )}
                  >
                    {column.render ? column.render(row) : String(column.value(row) ?? '—')}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
